import React, { useEffect, useMemo, useRef, useState } from "react";

const MultiSelectFilter = ({
  options,
  selectedValues,
  onChange,
  label = "Status",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e) => e.key === "Escape" && setIsOpen(false);
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [isOpen]);

  const handleToggle = (value) => {
    if (value === "All") {
      onChange(["All"]);
      return;
    }
    let newSelection = selectedValues.filter((v) => v !== "All");
    if (newSelection.includes(value)) {
      newSelection = newSelection.filter((v) => v !== value);
      if (newSelection.length === 0) newSelection = ["All"];
    } else {
      newSelection.push(value);
    }
    onChange(newSelection);
  };

  const isAllSelected =
    selectedValues.includes("All") || selectedValues.length === 0;

  const { primaryLabel, extraCount } = useMemo(() => {
    if (isAllSelected) return { primaryLabel: "All", extraCount: 0 };
    const [first, ...rest] = selectedValues;
    return { primaryLabel: first, extraCount: rest.length };
  }, [selectedValues, isAllSelected]);

  const handleClear = (e) => {
    e.stopPropagation();
    onChange(["All"]);
  };

  return (
    <div className="msf-root" ref={dropdownRef}>
      <button
        type="button"
        className={`msf-trigger ${isOpen ? "is-open" : ""}`}
        onClick={() => setIsOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span className="msf-label">{label}</span>
        <span className="msf-divider" aria-hidden />
        <span className="msf-value">
          <span className="msf-value-text">{primaryLabel}</span>
          {extraCount > 0 && (
            <span className="msf-badge">+{extraCount}</span>
          )}
        </span>
        <svg
          className="msf-chevron"
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden
        >
          <path
            d="M6 9l6 6 6-6"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {isOpen && (
        <div className="msf-panel" role="listbox" aria-multiselectable="true">
          <div className="msf-panel-header">
            <span className="msf-panel-title">{label}</span>
            {!isAllSelected && (
              <button
                type="button"
                className="msf-clear"
                onClick={handleClear}
              >
                Clear
              </button>
            )}
          </div>

          <ul className="msf-options">
            {options.map((option) => {
              const checked = selectedValues.includes(option);
              return (
                <li key={option}>
                  <label
                    className={`msf-option ${checked ? "is-checked" : ""}`}
                    role="option"
                    aria-selected={checked}
                  >
                    <input
                      type="checkbox"
                      className="msf-checkbox-input"
                      checked={checked}
                      onChange={() => handleToggle(option)}
                    />
                    <span className="msf-checkbox" aria-hidden>
                      <svg
                        viewBox="0 0 16 16"
                        width="10"
                        height="10"
                        fill="none"
                      >
                        <path
                          d="M3 8.5l3 3 7-7"
                          stroke="white"
                          strokeWidth="2.2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </span>
                    <span className="msf-option-text">{option}</span>
                  </label>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      <style>{`
        .msf-root {
          position: relative;
          display: inline-block;
          font-family: inherit;
        }

        .msf-trigger {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          min-width: 180px;
          padding: 9px 12px;
          background: #ffffff;
          border: 1px solid #E4E7EC;
          border-radius: 8px;
          color: #1F2937;
          font-size: 0.875rem;
          line-height: 1.2;
          cursor: pointer;
          transition: border-color 0.15s ease, box-shadow 0.15s ease,
            background-color 0.15s ease;
        }
        .msf-trigger:hover {
          border-color: #C7CBD3;
          background: #FAFAFB;
        }
        .msf-trigger:focus-visible,
        .msf-trigger.is-open {
          outline: none;
          border-color: #181D4C;
          box-shadow: 0 0 0 3px rgba(24, 29, 76, 0.12);
        }

        .msf-label {
          font-weight: 600;
          color: #6B7280;
          letter-spacing: 0.01em;
        }
        .msf-divider {
          width: 1px;
          align-self: stretch;
          background: #E4E7EC;
          margin: 2px 0;
        }
        .msf-value {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          flex: 1;
          min-width: 0;
        }
        .msf-value-text {
          font-weight: 500;
          color: #1F2937;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          max-width: 160px;
        }
        .msf-badge {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-width: 22px;
          height: 20px;
          padding: 0 6px;
          background: #181D4C;
          color: #ffffff;
          border-radius: 999px;
          font-size: 0.72rem;
          font-weight: 600;
          line-height: 1;
        }
        .msf-chevron {
          color: #6B7280;
          transition: transform 0.18s ease;
          flex-shrink: 0;
        }
        .msf-trigger.is-open .msf-chevron {
          transform: rotate(180deg);
        }

        .msf-panel {
          position: absolute;
          top: calc(100% + 6px);
          left: 0;
          z-index: 1000;
          min-width: 100%;
          width: max-content;
          max-width: 280px;
          background: #ffffff;
          border: 1px solid #E4E7EC;
          border-radius: 10px;
          box-shadow: 0 12px 28px rgba(15, 23, 42, 0.12),
            0 2px 6px rgba(15, 23, 42, 0.06);
          overflow: hidden;
          animation: msfFadeIn 0.14s ease-out;
        }
        @keyframes msfFadeIn {
          from {
            opacity: 0;
            transform: translateY(-4px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .msf-panel-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 10px 14px;
          border-bottom: 1px solid #F1F2F4;
          background: #FAFAFB;
        }
        .msf-panel-title {
          font-size: 0.78rem;
          font-weight: 600;
          color: #6B7280;
          text-transform: uppercase;
          letter-spacing: 0.04em;
        }
        .msf-clear {
          background: transparent;
          border: none;
          padding: 0;
          font-size: 0.78rem;
          font-weight: 600;
          color: #181D4C;
          cursor: pointer;
        }
        .msf-clear:hover {
          text-decoration: underline;
        }

        .msf-options {
          list-style: none;
          margin: 0;
          padding: 6px;
          max-height: 280px;
          overflow-y: auto;
        }

        .msf-option {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 8px 10px;
          border-radius: 6px;
          cursor: pointer;
          font-size: 0.875rem;
          color: #1F2937;
          transition: background-color 0.12s ease;
          user-select: none;
        }
        .msf-option:hover {
          background: #F4F5F7;
        }
        .msf-option.is-checked {
          background: #F1F2FA;
          color: #181D4C;
          font-weight: 500;
        }

        .msf-checkbox-input {
          position: absolute;
          opacity: 0;
          pointer-events: none;
          width: 0;
          height: 0;
        }
        .msf-checkbox {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 16px;
          height: 16px;
          border: 1.5px solid #C7CBD3;
          border-radius: 4px;
          background: #ffffff;
          transition: background-color 0.12s ease, border-color 0.12s ease;
          flex-shrink: 0;
        }
        .msf-checkbox svg {
          opacity: 0;
          transition: opacity 0.1s ease;
        }
        .msf-option.is-checked .msf-checkbox {
          background: #181D4C;
          border-color: #181D4C;
        }
        .msf-option.is-checked .msf-checkbox svg {
          opacity: 1;
        }

        .msf-option-text {
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .msf-options::-webkit-scrollbar {
          width: 8px;
        }
        .msf-options::-webkit-scrollbar-thumb {
          background: #E4E7EC;
          border-radius: 8px;
        }
        .msf-options::-webkit-scrollbar-thumb:hover {
          background: #C7CBD3;
        }
      `}</style>
    </div>
  );
};

export default MultiSelectFilter;
